// src/utils/dateUtils.js
/**
 * CENTRALIZED DATE UTILITIES
 * All dates use MM-DD-YYYY format (string)
 * Handles legacy formats: YY-MM-DD, YYYY-MM-DD
 */

/**
 * Format Date object to MM-DD-YYYY string
 * @param {Date|string|Timestamp} input - Date in any format
 * @returns {string} Date in MM-DD-YYYY format
 */
export const formatToMMDDYYYY = (input) => {
  let date;
  
  // Handle Firestore Timestamp
  if (input && typeof input.toDate === 'function') {
    date = input.toDate();
  }
  // Handle string input
  else if (typeof input === 'string') {
    if (input.includes('-')) {
      const parts = input.split('-');
      if (parts[0].length === 4) {
        // YYYY-MM-DD format
        const [year, month, day] = parts.map(p => parseInt(p));
        date = new Date(year, month - 1, day);
      } else if (parts[2].length === 4) {
        // MM-DD-YYYY format
        const [month, day, year] = parts.map(p => parseInt(p));
        date = new Date(year, month - 1, day);
      } else if (parts[0].length === 2 && parts[2].length === 2) {
        // Ambiguous: MM-DD-YY or YY-MM-DD — check if first part is valid month
        const firstNum = parseInt(parts[0]);
        const secondNum = parseInt(parts[1]);
        const thirdNum = parseInt(parts[2]);
        if (firstNum >= 1 && firstNum <= 12 && secondNum >= 1 && secondNum <= 31) {
          // MM-DD-YY
          date = new Date(2000 + thirdNum, firstNum - 1, secondNum);
        } else {
          // YY-MM-DD (legacy staff entries)
          const fullYear = firstNum < 100 ? 2000 + firstNum : firstNum;
          date = new Date(fullYear, secondNum - 1, thirdNum);
        }
      }
    } else {
      date = new Date(input);
    }
  }
  // Handle Date object
  else if (input instanceof Date) {
    date = input;
  }
  // Handle null/undefined
  else if (!input) {
    date = new Date();
  }
  
  if (!date || isNaN(date.getTime())) {
    console.error('Invalid date input:', input);
    return '';
  }
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}-${day}-${year}`;
};

/**
 * Parse date string to Date object
 * Handles: MM-DD-YYYY, YYYY-MM-DD, YY-MM-DD
 * @param {string} dateString - Date string
 * @returns {Date|null} JavaScript Date object or null if invalid
 */
export const parseMMDDYYYY = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }
  
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    console.error('Invalid date format:', dateString);
    return null;
  }
  
  let month, day, year;
  
  // Auto-detect format based on part lengths
  if (parts[0].length === 4) {
    // YYYY-MM-DD format (ISO)
    [year, month, day] = parts.map(p => parseInt(p));
  } else if (parts[2].length === 4) {
    // MM-DD-YYYY format (preferred)
    [month, day, year] = parts.map(p => parseInt(p));
  } else if (parts[0].length === 2 && parts[2].length === 2) {
    // Ambiguous: could be MM-DD-YY or YY-MM-DD
    // Distinguish by checking if first part is a valid month (1-12)
    const firstNum = parseInt(parts[0]);
    const secondNum = parseInt(parts[1]);
    const thirdNum = parseInt(parts[2]);
    if (firstNum >= 1 && firstNum <= 12 && secondNum >= 1 && secondNum <= 31) {
      // MM-DD-YY format (student startDates, care dates)
      month = firstNum;
      day = secondNum;
      year = 2000 + thirdNum;
    } else {
      // YY-MM-DD format (legacy staff entries)
      year = 2000 + firstNum;
      month = secondNum;
      day = thirdNum;
    }
  }
  
  if (!month || !day || !year || month < 1 || month > 12 || day < 1 || day > 31) {
    console.error('Invalid date parts:', { month, day, year, original: dateString });
    return null;
  }
  
  return new Date(year, month - 1, day);
};

/**
 * Get today's date in MM-DD-YYYY format
 * @returns {string} Today's date
 */
export const getTodayMMDDYYYY = () => {
  return formatToMMDDYYYY(new Date());
};

/**
 * Convert YYYY-MM-DD (date picker) to MM-DD-YYYY (Firestore)
 * @param {string} yyyymmdd - Date in YYYY-MM-DD format
 * @returns {string} Date in MM-DD-YYYY format
 */
export const convertPickerToFirestore = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [year, month, day] = yyyymmdd.split('-');
  return `${month}-${day}-${year}`;
};

/**
 * Convert MM-DD-YYYY (Firestore) to YYYY-MM-DD (date picker)
 * @param {string} mmddyyyy - Date in MM-DD-YYYY format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const convertFirestoreToPicker = (mmddyyyy) => {
  if (!mmddyyyy) return '';
  
  const date = parseMMDDYYYY(mmddyyyy);
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Normalize any date format to MM-DD-YYYY
 * Converts YY-MM-DD, YYYY-MM-DD, or MM-DD-YYYY to MM-DD-YYYY
 * @param {string} dateString - Date in any format
 * @returns {string|null} Date in MM-DD-YYYY format or null
 */
export const normalizeToMMDDYYYY = (dateString) => {
  if (!dateString) return null;
  
  const parsed = parseMMDDYYYY(dateString);
  if (!parsed) return null;
  
  return formatToMMDDYYYY(parsed);
};

/**
 * Check if a date string is valid MM-DD-YYYY format
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} True if valid MM-DD-YYYY format
 */
export const isValidMMDDYYYY = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return false;
  
  const pattern = /^\d{2}-\d{2}-\d{4}$/;
  if (!pattern.test(dateStr)) return false;
  
  const date = parseMMDDYYYY(dateStr);
  return date !== null && !isNaN(date.getTime());
};

/**
 * Get date range for reports (last N days)
 * @param {number} days - Number of days to go back
 * @returns {Object} Object with startDate and endDate in MM-DD-YYYY
 */
export const getDateRangeMMDDYYYY = (days = 30) => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - days);
  
  return {
    startDate: formatToMMDDYYYY(startDate),
    endDate: formatToMMDDYYYY(today)
  };
};

/**
 * Compare two date strings
 * @param {string} date1 - First date (any format)
 * @param {string} date2 - Second date (any format)
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDatesMMDDYYYY = (date1, date2) => {
  const d1 = parseMMDDYYYY(date1);
  const d2 = parseMMDDYYYY(date2);
  
  if (!d1 || !d2) return 0;
  
  if (d1 < d2) return -1;
  if (d1 > d2) return 1;
  return 0;
};

/**
 * Get day of week from date string
 * @param {string} dateStr - Date in any format
 * @returns {string} Day name (e.g., "Monday")
 */
export const getDayOfWeekMMDDYYYY = (dateStr) => {
  const date = parseMMDDYYYY(dateStr);
  if (!date) return '';
  
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

/**
 * Check if date is today
 * @param {string} dateStr - Date in any format
 * @returns {boolean} True if date is today
 */
export const isTodayMMDDYYYY = (dateStr) => {
  const today = getTodayMMDDYYYY();
  const normalized = normalizeToMMDDYYYY(dateStr);
  return normalized === today;
};

/**
 * Get week date range (Monday to Sunday)
 * @param {Date} date - Any date in the week
 * @returns {Array} Array of 7 dates in MM-DD-YYYY format
 */
export const getWeekDatesMMDDYYYY = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  
  const monday = new Date(d.setDate(diff));
  
  const weekDates = [];
  for (let i = 0; i < 7; i++) {
    const weekDate = new Date(monday);
    weekDate.setDate(monday.getDate() + i);
    weekDates.push(formatToMMDDYYYY(weekDate));
  }
  
  return weekDates;
};

/**
 * Check if a date has passed (is before today)
 * @param {string} dateString - Date in any format
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (dateString) => {
  const date = parseMMDDYYYY(dateString);
  if (!date) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  return date < today;
};

/**
 * Check if date is today or future
 * @param {string} dateString - Date in any format
 * @returns {boolean} True if date is today or future
 */
export const isTodayOrFuture = (dateString) => {
  const date = parseMMDDYYYY(dateString);
  if (!date) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  return date >= today;
};

/**
 * Check if student should be shown today based on start date
 * @param {string} startDate - Student's start date in any format
 * @returns {boolean} True if student should be shown today
 */
export const shouldShowStudentToday = (startDate) => {
  if (!startDate) return true;
  
  try {
    let dateToCompare;
    
    // Handle Firestore Timestamp
    if (startDate.toDate && typeof startDate.toDate === 'function') {
      dateToCompare = startDate.toDate();
    }
    // Handle string dates (any format)
    else if (typeof startDate === 'string') {
      dateToCompare = parseMMDDYYYY(startDate);
    }
    // Handle Date object
    else if (startDate instanceof Date) {
      dateToCompare = startDate;
    }
    
    if (!dateToCompare || isNaN(dateToCompare.getTime())) {
      console.warn('⚠️ Invalid start date format:', startDate);
      return true;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateToCompare.setHours(0, 0, 0, 0);
    
    return dateToCompare <= today;
  } catch (error) {
    console.error('Error in shouldShowStudentToday:', error, startDate);
    return true;
  }
};

/**
 * Convert HTML date input (YYYY-MM-DD) to MM-DD-YYYY
 * @param {string} htmlDate - Date from <input type="date">
 * @returns {string} Date in MM-DD-YYYY format
 */
export const htmlDateToMMDDYYYY = (htmlDate) => {
  if (!htmlDate) return null;
  return convertPickerToFirestore(htmlDate);
};

/**
 * Convert MM-DD-YYYY to HTML date input format (YYYY-MM-DD)
 * @param {string} usDate - Date in MM-DD-YYYY format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const mmdddyyyyToHtmlDate = (usDate) => {
  return convertFirestoreToPicker(usDate);
};

// Default export with all functions
const dateUtils = {
  formatToMMDDYYYY,
  parseMMDDYYYY,
  getTodayMMDDYYYY,
  convertPickerToFirestore,
  convertFirestoreToPicker,
  normalizeToMMDDYYYY,
  isValidMMDDYYYY,
  getDateRangeMMDDYYYY,
  compareDatesMMDDYYYY,
  getDayOfWeekMMDDYYYY,
  isTodayMMDDYYYY,
  getWeekDatesMMDDYYYY,
  isPastDate,
  isTodayOrFuture,
  shouldShowStudentToday,
  htmlDateToMMDDYYYY,
  mmdddyyyyToHtmlDate
};
export default dateUtils;